/**
 * JumpRoutes context — the app-tier seam that feeds the genome-derived Cmd-K
 * jump targets into the lazy IDE chunk (#142).
 *
 * The IDE command surface used to carry a hand-frozen `JUMP_ROUTES` literal
 * that drifted from the genome. The jump targets are now DERIVED from the
 * composed studio genome's resolved route table via {@link deriveJumpRoutes} —
 * but the derivation runs in `App.tsx` (which already calls
 * `resolveRoutes(genome, plugins)`, the gate-4 route table), NOT inside the IDE
 * chunk: importing the genome barrel into `IdeShell` would eagerly pull the
 * dock-panel-registering plugins (`dashboardPanelPlugin`/`containersPanelPlugin`)
 * into the lazy chunk and perturb the dock registry the shell reads at layout
 * time. So the resolved jump targets flow DOWN through this context instead.
 *
 * The default is a frozen empty array (a stable ref → selector-loop safe): a
 * bare `IdeShell` render with no provider simply lists no route jumps.
 *
 * @module @ubiclouds/v5-studio/ide/jump-routes-context
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { JumpRoute } from './jump-routes.js';

const EMPTY: readonly JumpRoute[] = Object.freeze([]);

const JumpRoutesContext = createContext<readonly JumpRoute[]>(EMPTY);

export function JumpRoutesProvider({
  value,
  children,
}: {
  readonly value: readonly JumpRoute[];
  readonly children: ReactNode;
}): ReactNode {
  return <JumpRoutesContext.Provider value={value}>{children}</JumpRoutesContext.Provider>;
}

/** The genome-derived Cmd-K jump targets (empty outside a provider). */
export function useJumpRoutes(): readonly JumpRoute[] {
  return useContext(JumpRoutesContext);
}
